import React, { useState } from "react";
import InfectedFileNotice from "./InfectedFileNotice.mjs";

export const PdfSupportingViewer = ({ pdfUrl, description, fileName, infected = false, noticeAudience = 'claimant' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleViewInNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank');
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (pdfUrl) {
      const printWindow = window.open(pdfUrl, '_blank');
      printWindow.addEventListener('load', () => printWindow.print());
    }
  };

  // Checked before !pdfUrl: an infected file's url is null too, but "No PDF
  // selected" would hide that the virus scan removed it.
  if (infected) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {fileName && <p className="text-sm font-medium text-gray-700 mb-2 break-words">{fileName}</p>}
        {description && <p className="text-sm text-gray-600 mb-2">{description}</p>}
        <InfectedFileNotice audience={noticeAudience} className="text-sm" />
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-500">No PDF selected</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {description && (
        <div className="bg-gray-50 border-b border-gray-200 p-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Document Description:</h4>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      )}

      {/* Phones: View Full Size on its own row, Download | Print below, all
          44px. From sm up this is the original wrapping row. */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 p-3 sm:p-4 border-b border-gray-200 no-print">
        <button
          className="col-span-2 min-h-[44px] sm:min-h-0 px-4 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
          onClick={handleViewInNewTab}
        >
          View Full Size
        </button>
        <button
          className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          onClick={handleDownload}
        >
          Download
        </button>
        <button
          className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          onClick={handlePrint}
        >
          Print
        </button>
      </div>

      <div className="relative">
        {loading && !error && (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <span>Loading PDF...</span>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <p className="text-gray-600 text-sm">Unable to preview this document inline.</p>
            <button
              className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
              onClick={handleViewInNewTab}
            >
              Open in New Tab
            </button>
          </div>
        )}

        {!error && (
          <iframe
            src={pdfUrl}
            title={fileName || 'PDF Document'}
            width="100%"
            // 600px was most of a phone screen and trapped page scrolling
            // inside the frame; desktop keeps 600px.
            className="h-[480px] sm:h-[600px]"
            style={{ border: 'none', display: loading ? 'none' : 'block' }}
            onLoad={() => setLoading(false)}
            onError={() => { setError(true); setLoading(false); }}
          />
        )}
      </div>

      <div className="print-only">
        <div className="p-4 border-t border-gray-200 text-sm text-gray-600">
          {fileName && <p><strong>File:</strong> {fileName}</p>}
          <p><strong>Document URL:</strong> {pdfUrl}</p>
          <p><strong>Printed on:</strong> {new Date().toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PdfSupportingViewer;
